import React, { useState, useRef, useEffect } from 'react';
import { ArrowUpTrayIcon, ArrowLeftIcon, PhotoIcon, TableCellsIcon, ArrowDownTrayIcon, CloudArrowUpIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import './ImportExportModal.css';
import { API_BASE_URL } from '../config';

const ImportExportModal = ({ close, backendService }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [file, setFile] = useState(null);
  const [runInference, setRunInference] = useState(true);
  const [skipExisting, setSkipExisting] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [job, setJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [report, setReport] = useState([]);
  const [metricsFile, setMetricsFile] = useState(null);
  const [isMetricsUploading, setIsMetricsUploading] = useState(false);
  const [metricsStatus, setMetricsStatus] = useState(null);
  const fileInputRef = useRef(null);
  const metricsFileInputRef = useRef(null);
  const recentLogs = logs.slice(-50);
  const formatLogTime = (value) => {
    if (!value) return '--:--:--';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '--:--:--';
    }
    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
  };

  // Check for existing job on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await backendService.getImportStatus();
        if (status) {
          setLogs(status.logs || []);
          setReport(status.result || []);
          if (status.status === 'pending' || status.status === 'processing') {
             // If a job is running, assume it's for photos as that's the only async one currently
             setSelectedCategory('photos');
             setJob(status);
             setIsUploading(true);
             setUploadStatus(status.progress?.message || 'Processing...');
          } else if (status.status === 'completed') {
             // Optionally show the completed job, but maybe let the user navigate?
             // For now, if completed recently, we might want to show it, but better to start fresh unless navigated.
             // If we want to show history, we'd need more logic. 
             // Let's just store it in case they go to 'photos'
             setJob(status);
          }
        }
      } catch (err) {
        console.error('Failed to check import status:', err);
      }
    };
    checkStatus();
  }, [backendService]);

  // WebSocket connection
  useEffect(() => {
    let wsUrl;
    if (API_BASE_URL) {
      const apiUrl = new URL(API_BASE_URL || window.location.origin);
      const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${apiUrl.host}/ws`;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/ws`;
    }

    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        // console.log('WebSocket message received:', message);
        if (message.type === 'import_progress') {
          // If a progress update comes in, switch to photos tab if not there?
          // Or just update state implicitly.
          if (!selectedCategory && message.job.status === 'processing') {
             setSelectedCategory('photos');
          }
          
          setJob(message.job);
          setLogs(message.job.logs || []);
          setReport(message.job.result || []);
          if (message.job.status === 'completed') {
             setIsUploading(false);
             setUploadStatus('Import successful!');
             setFile(null);
          } else if (message.job.status === 'failed') {
             setIsUploading(false);
             setUploadStatus('Import failed: ' + message.job.error);
          } else {
             setUploadStatus(message.job.progress?.message);
          }
        }
      } catch (e) {
        console.error("Error parsing websocket message", e);
      }
    };

    return () => {
      ws.close();
    };
  }, [selectedCategory]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && (selectedFile.type === 'application/zip' || selectedFile.name.endsWith('.zip'))) {
      setFile(selectedFile);
      setUploadStatus(null);
    } else {
      alert('Please select a valid ZIP file.');
      setFile(null);
    }
  };

  const handleMetricsFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && (selectedFile.type === 'text/csv' || selectedFile.name.toLowerCase().endsWith('.csv'))) {
      setMetricsFile(selectedFile);
      setMetricsStatus(null);
    } else if (selectedFile) {
      alert('Please select a CSV file.');
      setMetricsFile(null);
      if (metricsFileInputRef.current) {
        metricsFileInputRef.current.value = '';
      }
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadStatus('Starting import...');
    setLogs([]);
    setReport([]);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('runInference', runInference);
    formData.append('skipExisting', skipExisting);

    try {
      await backendService.importData(formData);
      // The backend now returns immediately with a job ID.
      // We rely on WebSocket for updates, but we can set initial state here.
      setUploadStatus('Import started...');
    } catch (error) {
      console.error('[ImportExportModal] Import failed:', error);
      setUploadStatus('Import failed: ' + (error.response?.data?.message || error.message));
      setIsUploading(false);
    }
  };

  const handleMetricsImport = async () => {
    if (!metricsFile) return;

    setIsMetricsUploading(true);
    setMetricsStatus({ text: 'Uploading metrics...', isError: false });

    const formData = new FormData();
    formData.append('file', metricsFile);

    try {
      const response = await backendService.importMetrics(formData);
      const processedDays = response?.processedDays ?? 0;
      const totalMeasurements = response?.totalMeasurements ?? 0;
      setMetricsStatus({
        text: `Imported ${totalMeasurements} measurement${totalMeasurements === 1 ? '' : 's'} across ${processedDays} day${processedDays === 1 ? '' : 's'}.`,
        isError: false
      });
      setMetricsFile(null);
      if (metricsFileInputRef.current) {
        metricsFileInputRef.current.value = '';
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Unknown error';
      setMetricsStatus({ text: `Metrics import failed: ${message}`, isError: true });
    } finally {
      setIsMetricsUploading(false);
    }
  };

  const renderCategorySelection = () => (
    <div className="category-selection">
      <div className="category-card" onClick={() => setSelectedCategory('photos')}>
        <PhotoIcon className="category-icon" />
        <span className="category-title">Pictures</span>
      </div>
      <div className="category-card" onClick={() => setSelectedCategory('metrics')}>
        <TableCellsIcon className="category-icon" />
        <span className="category-title">Metrics</span>
      </div>
       <div className="category-card" onClick={() => setSelectedCategory('export')}>
        <ArrowDownTrayIcon className="category-icon" />
        <span className="category-title">Export Data</span>
      </div>
    </div>
  );

  const renderPhotosImport = () => (
    <div className="import-section fade-in">
      <div className="section-header">
        <button className="back-button" onClick={handleBackToCategories} disabled={isUploading}>
          <ArrowLeftIcon className="back-icon" /> Back
        </button>
        <h3>Import Pictures</h3>
      </div>
      
      <div className="upload-container">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".zip"
          style={{ display: 'none' }}
        />
        <div 
          className={`file-drop-zone ${file ? 'has-file' : ''} ${isUploading ? 'disabled' : ''}`}
          onClick={() => !isUploading && fileInputRef.current.click()}
        >
          {file ? (
            <div className="file-info">
              <DocumentTextIcon className="file-icon" />
              <div className="file-details">
                <span className="file-name">{file.name}</span>
                <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
              <button 
                className="change-file-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current.click();
                }}
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <CloudArrowUpIcon className="upload-icon" />
              <span className="upload-text">Click to select ZIP file</span>
              <span className="upload-hint">Format: <code>2023-10-27_14-30-00.jpg</code> inside zip</span>
            </>
          )}
        </div>
      </div>

      <div className="options-panel">
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={skipExisting}
            onChange={(e) => setSkipExisting(e.target.checked)}
            disabled={isUploading}
          />
          <div className="checkbox-content">
            <span className="checkbox-title">Skip Existing</span>
            <span className="checkbox-desc">Don't overwrite photos if they already exist for that day</span>
          </div>
        </label>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={runInference}
            onChange={(e) => setRunInference(e.target.checked)}
            disabled={isUploading}
          />
          <div className="checkbox-content">
            <span className="checkbox-title">Run AI Analysis</span>
            <span className="checkbox-desc">Analyze body composition and measurements automatically</span>
          </div>
        </label>
      </div>

      <button
        className="primary-action-btn"
        onClick={handleImport}
        disabled={(!file && !isUploading) || (isUploading && !job)} 
      >
        {isUploading ? (
          <><span>Importing...</span> <div className="spinner-small"></div></>
        ) : (
          <><span>Start Import</span> <ArrowUpTrayIcon className="btn-icon" /></>
        )}
      </button>

      {job && job.status === 'processing' && (
        <div className="progress-container">
            <div className="progress-bar">
                <div 
                    className="progress-fill" 
                    style={{ width: `${job.progress?.percentage || 0}%` }}
                ></div>
            </div>
            <div className="progress-text">
                {job.progress?.percentage}% - {job.progress?.message}
            </div>
        </div>
      )}

      {uploadStatus && !job && <div className={`status-message ${uploadStatus.includes('failed') ? 'error' : 'success'}`}>{uploadStatus}</div>}
      {job && job.status !== 'processing' && <div className={`status-message ${job.status === 'failed' ? 'error' : 'success'}`}>{uploadStatus}</div>}

      {recentLogs.length > 0 && (
        <div className="import-log">
          <h4>Activity Log</h4>
          <div className="log-entries">
            {recentLogs.map((entry, idx) => (
              <div className="log-entry" key={`${entry.timestamp}-${idx}`}>
                <span className="log-time">
                  {formatLogTime(entry.timestamp)}
                </span>
                <span className="log-message">{entry.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {report.length > 0 && (
        <div className="import-report">
          <h4>Import Report</h4>
          <div className="report-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Files</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {report.map((row, idx) => (
                  <tr key={`${row.date}-${row.status}-${idx}`}>
                    <td>{row.date || '—'}</td>
                    <td className={row.status === 'imported' ? 'status-imported' : row.status === 'skipped' ? 'status-skipped' : ''}>
                      {row.status === 'imported' ? 'Imported' : row.status === 'skipped' ? 'Skipped' : 'Unknown'}
                    </td>
                    <td>{typeof row.count === 'number' ? row.count : '—'}</td>
                    <td>{row.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderMetricsImport = () => (
    <div className="metrics-import-section fade-in">
      <div className="section-header">
        <button className="back-button" onClick={handleBackToCategories} disabled={isMetricsUploading}>
          <ArrowLeftIcon className="back-icon" /> Back
        </button>
        <h3>Import Metrics</h3>
      </div>
      
      <div className="upload-container">
        <input
          type="file"
          ref={metricsFileInputRef}
          onChange={handleMetricsFileChange}
          accept=".csv,text/csv"
          style={{ display: 'none' }}
        />
        <div 
          className={`file-drop-zone ${metricsFile ? 'has-file' : ''} ${isMetricsUploading ? 'disabled' : ''}`}
          onClick={() => !isMetricsUploading && metricsFileInputRef.current.click()}
        >
          {metricsFile ? (
            <div className="file-info">
              <TableCellsIcon className="file-icon" />
              <div className="file-details">
                <span className="file-name">{metricsFile.name}</span>
                <span className="file-size">{(metricsFile.size / 1024).toFixed(2)} KB</span>
              </div>
              <button 
                className="change-file-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  metricsFileInputRef.current.click();
                }}
              >
                Change
              </button>
            </div>
          ) : (
             <>
              <ArrowDownTrayIcon className="upload-icon" />
              <span className="upload-text">Click to select CSV file</span>
              <span className="upload-hint">Columns: <code>date, type_id, value</code></span>
            </>
          )}
        </div>
      </div>

      <div className="info-panel">
        <div className="csv-example-container">
          <span className="example-label">CSV Format Example:</span>
          <pre className="csv-code-block">
{`date,type_id,value
2024-11-06,weight,182.4
2024-11-06,fat_percentage,18.5
2024-11-07,weight,181.9`}
          </pre>
        </div>
        <div className="metrics-legend">
           <span className="legend-title">Supported Types:</span>
           <div className="legend-tags">
             <span className="tag">weight (lbs)</span>
             <span className="tag">fat_percentage (%)</span>
           </div>
        </div>
      </div>

      <button
        className="primary-action-btn"
        onClick={handleMetricsImport}
        disabled={!metricsFile || isMetricsUploading}
      >
        {isMetricsUploading ? (
          <><span>Importing...</span> <div className="spinner-small"></div></>
        ) : (
          <><span>Import Metrics</span> <ArrowUpTrayIcon className="btn-icon" /></>
        )}
      </button>
      {metricsStatus && (
        <div className={`status-message ${metricsStatus.isError ? 'error' : 'success'}`}>
          {metricsStatus.text}
        </div>
      )}
    </div>
  );

  const renderExport = () => (
    <div className="export-section">
      <button className="back-button" onClick={handleBackToCategories}>
        <ArrowLeftIcon className="back-icon" /> Back
      </button>
      <h3>Export Data</h3>
      <p className="instruction-text">Export functionality is coming soon.</p>
    </div>
  );

  return (
    <div className="import-export-modal">
      <div className="modal-header">
        <h2>Import / Export Data</h2>
      </div>
      <div className="modal-content">
        {!selectedCategory && renderCategorySelection()}
        {selectedCategory === 'photos' && renderPhotosImport()}
        {selectedCategory === 'metrics' && renderMetricsImport()}
        {selectedCategory === 'export' && renderExport()}
      </div>
    </div>
  );
};

export default ImportExportModal;
